import React, { useState, useEffect } from "react";
import "@/App.css";
import "@/tv-mode.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { api } from "@/api";
import { resolveMediaUrl } from "@/utils/helpers";
import { ViewerAuthProvider } from "@/contexts/ViewerAuthContext";
import { TVModeProvider } from "@/contexts/TVModeContext";

// Common Components
import { Navigation, Footer } from "@/components/common";
import InstallPrompt from "@/components/common/InstallPrompt";

// Page Components
import { HomePage, SportPage, LeaguePage, FixturesPage, AboutPage, RadioPage, PodcastPage, NewsPage, NewsArticlePage, FindGamePage, LiveTVPage, GameWatchPage } from "@/components/pages";
import AdvertiserPage from "@/components/pages/AdvertiserPage";
import AdPartnerPage from "@/components/pages/AdPartnerPage";
import AllGamesPage from "@/components/pages/AllGamesPage";
import BookingPage from "@/components/pages/BookingPage";
import PreferencesPage from "@/components/pages/PreferencesPage";
import SignupPage from "@/components/pages/SignupPage";
import AuthCallback from "@/components/pages/AuthCallback";
import ManageDevicesPage from "@/components/pages/ManageDevicesPage";
import ActivateTVPage from "@/components/pages/ActivateTVPage";

// Admin Components
import { AdminPanel, VerifyEmail, ResetPassword } from "@/components/admin/AdminComponents";

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function App() {
  const [sports, setSports] = useState([]);
  const [leagues, setLeagues] = useState([]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [sportsRes, leaguesRes, settingsRes] = await Promise.all([
          api.getSports(),
          api.getLeagues(),
          api.getSettings()
        ]);
        setSports(sportsRes.data);
        setLeagues(leaguesRes.data);
        
        // Load favicon if configured
        const faviconUrl = settingsRes.data?.favicon_url;
        if (faviconUrl) {
          const link = document.querySelector("link[rel~='icon']") || document.createElement('link');
          link.rel = 'icon';
          link.href = resolveMediaUrl(faviconUrl);
          document.head.appendChild(link);
        }
        
        // Load Google Analytics if configured
        const gaId = settingsRes.data?.google_analytics_id;
        if (gaId && gaId.startsWith('G-')) {
          // Load gtag.js
          const script1 = document.createElement('script');
          script1.async = true;
          script1.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
          document.head.appendChild(script1);
          
          // Initialize gtag
          window.dataLayer = window.dataLayer || [];
          function gtag(){window.dataLayer.push(arguments);}
          window.gtag = gtag;
          gtag('js', new Date());
          gtag('config', gaId);
        }
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };
    loadData();
  }, []);

  return (
    <HelmetProvider>
      <TVModeProvider>
      <ViewerAuthProvider>
        <div className="App bg-main min-h-screen flex flex-col">
          <BrowserRouter>
            <ScrollToTop />
            <Navigation sports={sports} leagues={leagues} />
            <InstallPrompt />
            <main className="flex-1">
              <Routes>
              <Route path="/" element={<HomePage sports={sports} />} />
              <Route path="/sport/:slug" element={<SportPage sports={sports} />} />
              <Route path="/league/:slug" element={<LeaguePage leagues={leagues} />} />
              <Route path="/games/:type" element={<AllGamesPage />} />
              <Route path="/fixtures" element={<FixturesPage sports={sports} leagues={leagues} />} />
              <Route path="/live" element={<LiveTVPage />} />
              <Route path="/watch/:gameId" element={<GameWatchPage />} />
              <Route path="/find-game" element={<FindGamePage />} />
              <Route path="/booking" element={<BookingPage />} />
              <Route path="/booking/new" element={<BookingPage />} />
              <Route path="/booking/dashboard" element={<BookingPage />} />
              <Route path="/preferences" element={<PreferencesPage />} />
              <Route path="/devices" element={<ManageDevicesPage />} />
              <Route path="/activate" element={<ActivateTVPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/radio" element={<RadioPage />} />
              <Route path="/podcast" element={<PodcastPage />} />
              <Route path="/news" element={<NewsPage />} />
              <Route path="/news/:slug" element={<NewsArticlePage />} />
              <Route path="/verify" element={<VerifyEmail />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/advertise" element={<AdPartnerPage />} />
              <Route path="/advertiser/*" element={<AdvertiserPage />} />
              <Route path="/admin/*" element={<AdminPanel sports={sports} />} />
            </Routes>
          </main>
          <Footer />
        </BrowserRouter>
      </div>
      </ViewerAuthProvider>
      </TVModeProvider>
    </HelmetProvider>
  );
}

export default App;
